// src/components/timeBoxes/AIRecommendationsSection.tsx
import React, { useState } from 'react';
import { TimeRecommendation } from '../../../types/scheduleRecommendation';
import AIRecommendationCard from './AIRecommendationCard';
import { Sparkles, AlertCircle, X } from '../../../components/common/icons';

interface AIRecommendationsSectionProps {
  /** AI 建議列表 */
  recommendations: TimeRecommendation[];

  /** 載入狀態 */
  isLoading: boolean;

  /** 錯誤訊息 */
  error: string | null;

  /** 選擇建議的回調 */
  onSelectRecommendation: (recommendation: TimeRecommendation) => void;

  /** 當前選中的建議索引 */
  selectedIndex?: number;

  /** 是否顯示錯誤提示（預設 true） */
  showErrorBanner?: boolean;

  /** 關閉錯誤提示的回調 */
  onDismissError?: () => void;
}

/**
 * AI 建議區塊組件
 * 顯示載入狀態、建議列表或錯誤訊息
 */
const AIRecommendationsSection: React.FC<AIRecommendationsSectionProps> = ({
  recommendations,
  isLoading,
  error,
  onSelectRecommendation,
  selectedIndex,
  showErrorBanner = true,
  onDismissError
}) => {
  const [isDismissed, setIsDismissed] = useState(false);

  // 處理錯誤關閉
  const handleDismissError = () => {
    setIsDismissed(true);
    if (onDismissError) {
      onDismissError();
    }

    // 3 秒後自動淡出
    setTimeout(() => {
      setIsDismissed(false);
    }, 3000);
  };

  // 載入狀態
  if (isLoading) {
    return (
      <div className="mb-6 p-4 bg-sky-50 rounded-lg border border-sky-100">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={18} className="text-sky-600 animate-pulse" />
          <span className="text-sm font-medium text-sky-900">
            Getting suggestions...
          </span>
        </div>
        <div className="space-y-3">
          {/* 骨架屏 */}
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-24 bg-sky-100/50 rounded-lg animate-pulse"
            ></div>
          ))}
        </div>
      </div>
    );
  }

  // 錯誤狀態（靜默降級，顯示可關閉的提示）
  if (error && showErrorBanner && !isDismissed) {
    return (
      <div
        className={`mb-6 p-3 bg-yellow-50 rounded-lg border border-yellow-200 flex items-start gap-2 transition-opacity duration-300 ${
          isDismissed ? 'opacity-0' : 'opacity-100'
        }`}
      >
        <AlertCircle size={16} className="text-yellow-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm text-yellow-800">
            Can't load suggestions right now
          </p>
        </div>
        <button
          type="button"
          onClick={handleDismissError}
          className="text-yellow-600 hover:text-yellow-800 transition-colors"
          aria-label="Dismiss"
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  // 有建議時顯示
  if (recommendations.length > 0) {
    return (
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={18} className="text-sky-600" />
          <h3 className="text-sm font-semibold text-gray-900">
            Recommendations
          </h3>
        </div>
        <div className="space-y-3">
          {recommendations.map((rec, index) => (
            <AIRecommendationCard
              key={index}
              recommendation={rec}
              isSelected={selectedIndex === index}
              onSelect={() => onSelectRecommendation(rec)}
            />
          ))}
        </div>
      </div>
    );
  }

  // 沒有建議且沒有載入中，不顯示任何內容
  return null;
};

export default AIRecommendationsSection;
